import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { ApplicationStore } from "src/store";
import { postTestDriveRequest } from "src/store/actions/testDrive";
import { useHistory } from "react-router";
import { getTestDriveDate, getTestDriveTime } from "src/store/selectors/testDrive";
import moment from "moment";
import {
    getCanadianMarketingOptIn,
    getEmail,
    getFirstName,
    getLastName,
    getMobilePhone,
} from "src/store/selectors/person";
import OSDatepicker from "Shared/Datepicker";
import Device from "current-device";
import OSTimepicker from "Shared/Timepicker";
import { getAccountDisclaimer } from "src/store/selectors/accountDisclaimers";
import { getAccountCountry } from "src/store/selectors/account";
import { cleanPhone, validateEmail, validatePhone } from "Shared/Form/validation";
import TermsAndPrivacy from "src/components/UserAccount/TermsAndPrivacy";
import { SecondaryButton } from "Shared/Buttons";
import MaskedInput from "react-text-mask";
import { analyticsFEA } from "src/AnalyticsFEA";
import { getVehiclesArrayForFEA } from "src/store/selectors/vehicles";

const TestDriveForm = ({
    account,
    region,
    emailAddress,
    firstName,
    lastName,
    methodOfContact,
    phoneNumber,
    canadianMarketingOptIn,
    testDriveDate,
    testDriveTime,
    testDriveCtaText,
    uuid,
    vehiclesForFEA,
    submitForm,
}: ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
    const { t } = useTranslation();
    const history = useHistory();
    const formRef = React.useRef<HTMLDivElement>(null);

    // Required fields
    const [date, setDate] = useState(testDriveDate ? moment(testDriveDate).toDate() : undefined);
    const [time, setTime] = useState(testDriveTime);

    // Required field, only used in canada, default to to true because customer has already accepted
    const [optInTransactional, setOptInTransactional] = useState(true);

    // Optional fields
    const [first, setFirstName] = useState(firstName);
    const [last, setLastName] = useState(lastName);
    const [contactMethod, setContactMethod] = useState(methodOfContact);
    const [phone, setPhone] = useState(phoneNumber ?? "");
    const [email, setEmail] = useState(emailAddress ?? "");
    const [optInMarketing, setOptInMarketing] = useState(canadianMarketingOptIn);

    // UI state
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // Reset errors on required field change
    useEffect(() => {
        setError("");
    }, [date, time, optInTransactional]);

    useEffect(() => {
        analyticsFEA.engine.testDriveShown(uuid, vehiclesForFEA);
    }, [uuid, vehiclesForFEA]);

    const formStarted = useCallback(() => {
        analyticsFEA.engine.testDriveStarted(uuid, vehiclesForFEA);
    }, [uuid, vehiclesForFEA]);

    useEffect(() => {
        if (formRef && formRef.current) {
            formRef.current.addEventListener("click", formStarted, {
                once: true,
            });
        }
    }, [formStarted]);

    const handleSubmit = () => {
        // Validate required fields
        if (date === undefined) {
            setError(t("TestDriveForm.error.date"));
            return;
        }

        if (time === undefined) {
            setError(t("TestDriveForm.error.time"));
            return;
        }

        if (!optInTransactional) {
            setError(t("TestDriveForm.error.optInTransactional"));
            return;
        }

        // Disable submit button
        setLoading(true);

        analyticsFEA.engine.testDriveFinished(uuid, vehiclesForFEA);

        // Dispatch postTestDriveRequest action
        submitForm(
            {
                date: moment(date).format("YYYY-MM-DD"),
                time,
            },
            history,
            {
                // Person fields are optional, only patch valid updates
                ...(first && { first_name: first.trim() }),
                ...(last && { last_name: last.trim() }),
                ...(contactMethod === "sms" &&
                    phone &&
                    validatePhone(phone) && {
                        mobile_phone: cleanPhone(phone),
                        preferences: {
                            method_of_contact: contactMethod,
                            ...(region === "CA" && { opt_in_marketing: optInMarketing }),
                        },
                    }),
                ...(contactMethod === "email" &&
                    email &&
                    validateEmail(email) && {
                        email: email.trim(),
                        preferences: {
                            method_of_contact: contactMethod,
                            ...(region === "CA" && { opt_in_marketing: optInMarketing }),
                        },
                    }),
            },
            setError,
            setLoading,
            uuid
        );
    };

    return (
        <div className={"TestDriveForm"} ref={formRef}>
            {/* First & Last Name Fields */}
            <label className={"TestDriveForm__full-width TestDriveForm__section-label"}>
                {t("TestDriveForm.nameSection.Label")}
            </label>
            <input
                name={"given-name"}
                className={"TestDriveForm__first-name"}
                placeholder={t("TestDriveForm.firstName.placeholder")}
                value={first}
                onChange={(e) => setFirstName(e.target.value)}
            />
            <input
                name={"family-name"}
                placeholder={t("TestDriveForm.lastName.placeholder")}
                value={last}
                onChange={(e) => setLastName(e.target.value)}
            />

            {/* Preferred Contact Method Field & Phone or Email Field */}
            <label className={"TestDriveForm__full-width TestDriveForm__section-label"}>
                {t("TestDriveForm.contactSection.Label")}
            </label>
            <div className={"TestDriveForm__full-width"}>
                <label>
                    <input
                        name={"contact-method"}
                        type="radio"
                        checked={contactMethod === "sms"}
                        value={"sms"}
                        onChange={() => setContactMethod("sms")}
                    />
                    {t("TestDriveForm.phone.Label")}
                </label>
                <label>
                    <input
                        name={"contact-method"}
                        type="radio"
                        checked={contactMethod === "email"}
                        value={"email"}
                        onChange={() => setContactMethod("email")}
                    />
                    {t("TestDriveForm.email.Label")}
                </label>
                <div key={contactMethod} className={"TestDriveForm__preferred-contact"}>
                    {contactMethod === "sms" ? (
                        <MaskedInput
                            key={contactMethod}
                            placeholder={t("TestDriveForm.phone.placeholder")}
                            type="tel"
                            name="phone"
                            mask={["(", /\d/, /\d/, /\d/, ")", " ", /\d/, /\d/, /\d/, "-", /\d/, /\d/, /\d/, /\d/]}
                            guide={false}
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                        />
                    ) : (
                        <input
                            key={contactMethod}
                            type="email"
                            name="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={t("TestDriveForm.email.placeholder")}
                        />
                    )}
                </div>
            </div>

            {/* Date & Time Fields */}
            <label className={"TestDriveForm__full-width TestDriveForm__section-label"}>
                {t("TestDriveForm.appointmentSection.Label")}
            </label>
            <OSDatepicker
                selectedDate={date}
                reportUp={setDate}
                placeholderText={t("TestDriveForm.date.placeholder")}
                minDate={new Date()}
                inputDisabled={Device.mobile()}
            />
            <OSTimepicker reportUp={setTime} placeholderText={t("TestDriveForm.time.placeholder")} value={time} />

            {/* Error Message */}
            {error && <div className={"TestDriveForm__error-message TestDriveForm__full-width"}>{error}</div>}

            {/* Submit Button */}
            <SecondaryButton
                name="submit"
                className={"TestDriveForm__full-width"}
                onClick={handleSubmit}
                disabled={loading}
            >
                {loading ? t("TestDriveForm.submit.loading") : testDriveCtaText}
            </SecondaryButton>

            {/* Disclaimer */}
            <div className={"TestDriveForm__full-width"}>
                <TermsAndPrivacy
                    region={region}
                    account={account}
                    optInTransactional={optInTransactional}
                    optInMarketing={optInMarketing}
                    onAgreeTransactionalClick={setOptInTransactional}
                    onAgreePromotionalClick={setOptInMarketing}
                />
            </div>
        </div>
    );
};

const mapStateToProps = (state: ApplicationStore) => ({
    account: state.account,
    region: getAccountCountry(state),
    uuid: state.deal.uuid!,
    testDriveDate: getTestDriveDate(state),
    testDriveTime: getTestDriveTime(state),
    methodOfContact: state.deal.person?.preferences?.method_of_contact ?? "sms",
    firstName: getFirstName(state),
    lastName: getLastName(state),
    phoneNumber: getMobilePhone(state),
    emailAddress: getEmail(state),
    canadianMarketingOptIn: getCanadianMarketingOptIn(state),
    testDriveCtaText: getAccountDisclaimer(state, "test_drive_heading"),
    vehiclesForFEA: getVehiclesArrayForFEA(state),
});

const mapDispatchToProps = {
    submitForm: postTestDriveRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(TestDriveForm);
